import { useCallback, useEffect, useMemo, useState } from 'react'
import { readChartLayout, type ChartLayoutPreferences } from '@/lib/screenerChartPreferences'

export interface ChartLayoutPreferenceControls {
  value: ChartLayoutPreferences
  setOrder: (order: string[]) => void
  setCollapsed: (collapsed: string[]) => void
  setHeight: (chartId: string, height: number) => void
  setWidth: (chartId: string, width: number) => void
}

/** One owner keeps hidden and mounted panes in the same preset snapshot. */
export function useChartLayoutPreferences(storageKey: string) {
  const saved = useMemo(() => readChartLayout(storageKey), [storageKey])
  const [state, setState] = useState({ storageKey, value: saved })
  const value = state.storageKey === storageKey ? state.value : saved

  useEffect(() => {
    if (state.storageKey !== storageKey) return
    try { localStorage.setItem(`${storageKey}:layout`, JSON.stringify(state.value)) } catch { /* Optional storage. */ }
  }, [state, storageKey])

  const change = useCallback((update: (current: ChartLayoutPreferences) => ChartLayoutPreferences) => {
    setState((current) => ({ storageKey, value: update(current.storageKey === storageKey ? current.value : saved) }))
  }, [saved, storageKey])
  const apply = useCallback((next: ChartLayoutPreferences) => change(() => ({
    order: [...next.order], collapsed: [...next.collapsed], heights: { ...next.heights }, widths: { ...next.widths },
  })), [change])
  const controls = useMemo<ChartLayoutPreferenceControls>(() => ({
    value,
    setOrder: (order) => change((current) => ({ ...current, order })),
    setCollapsed: (collapsed) => change((current) => ({ ...current, collapsed })),
    setHeight: (id, height) => change((current) => ({ ...current, heights: { ...current.heights, [id]: height } })),
    setWidth: (id, width) => change((current) => ({ ...current, widths: { ...current.widths, [id]: width } })),
  }), [value, change])
  return { controls, apply }
}
